import { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import * as Yup from 'yup';

import Button from '@/components/Button';
import {
	CfCaptchaWidget,
	useCfTurnstileChallenge,
	useLoadCfTurnstileScript
} from '@/components/CfCaptchaWidget/index.js';
import ErrorMessage from '@/components/ErrorMessage';
import Input from '@/components/form/Input';
import Check from '@/icons/success.svg';
import susbcriptionModel from '@/models/subscription';
import routes from '@/routes';

function Form() {
	const { t } = useTranslation();
	const [success, setSuccess] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const [loading, setLoading] = useState(false);

	useLoadCfTurnstileScript();

	const validationSchema = Yup.object({
		email: Yup.string().email().required()
	});

	const methods = useForm({
		resolver: yupResolver(validationSchema),
		mode: 'onChange',
		defaultValues: {
			email: ''
		}
	});

	const {
		cfChallengeToken,
		cfChallengeCompleted,
		cfIdempotencyKey,
		triggerChallenge
	} = useCfTurnstileChallenge({
		action: 'unsubscribe'
	});

	async function onSubmit(values) {
		const formValues = { ...values, cfChallengeToken, cfIdempotencyKey };
		setErrorMessage('');
		setLoading(true);

		try {
			const response = await susbcriptionModel.cancelByEmail(formValues);

			if (response?.code === 'invalid_email') {
				return setErrorMessage(t('unsubscribe.invalidEmail'));
			}

			if (response?.code === 'already_unsubscribed') {
				return setErrorMessage(t('unsubscribe.alreadyUnsubscribed'));
			}

			if (response.success) {
				return setSuccess(true);
			}
		} catch (error) {
			setErrorMessage(t('unsubscribe.genericError'));
		} finally {
			setLoading(false);
		}
	}

	const email = methods.watch('email');

	useEffect(() => {
		if (email.includes('@')) {
			triggerChallenge();
		}
	}, [email]);

	return (
		<section className="relative z-10 flex items-center justify-center rounded-xl border border-grey-200 bg-white shadow-lg lg:mx-[106px]">
			<div className="flex w-full flex-col items-center justify-center gap-10 px-4 py-6 lg:px-7 lg:py-12">
				{success ? (
					<div className="flex flex-col items-center gap-4 lg:gap-7">
						<Check />
						<div className="flex flex-col items-center gap-1">
							<h3 className="text-center text-xl font-semibold lg:leading-8">
								{t('unsubscribe.form.fullfilled.title')}
							</h3>
							<p className="text-center leading-7 text-grey-500">
								{t('unsubscribe.form.fullfilled.description')}
							</p>
						</div>

						<Button
							variant="outline"
							onClick={() => {
								setSuccess(false);
							}}
						>
							{t('unsubscribe.form.back')}
						</Button>
					</div>
				) : (
					<div className="m-0 flex w-full flex-col items-center">
						<FormProvider {...methods}>
							<form
								onSubmit={methods.handleSubmit(onSubmit)}
								className="mb-7 flex w-full flex-col items-center justify-center gap-4 rounded-lg bg-white lg:max-w-[606px] lg:gap-7"
							>
								<Input
									label={`${t('unsubscribe.form.email')} *`}
									name="email"
									placeholder={`${t('unsubscribe.form.placeholder.email')}`}
									size="lg"
								/>

								<div className="lg:col-span-2 lg:grid lg:grid-cols-1">
									<CfCaptchaWidget />
								</div>

								{errorMessage && (
									<ErrorMessage className="rounded bg-light-red px-2 py-1">
										{errorMessage}
									</ErrorMessage>
								)}

								<Button
									className="w-full lg:w-auto"
									type="submit"
									loading={loading}
									disabled={!cfChallengeCompleted}
									aria-label={`${t('unsubscribe.form.button')} ${t('labels.form')}`}
								>
									{t('unsubscribe.form.button')}
								</Button>
							</form>
						</FormProvider>

						<p className="text-center text-sm leading-6 text-grey-500 lg:w-[456px]">
							<Trans
								t={t}
								i18nKey="unsubscribe.form.note"
								components={{
									anchor: (
										<Link
											className="font-bold text-red"
											to={routes.contact}
										/>
									)
								}}
							/>
						</p>
					</div>
				)}
			</div>
		</section>
	);
}

export default Form;
